export type CommandPendingVariant = 'allowance' | 'models' | 'pipeline' | 'decisions';

interface PendingSpec {
  title: string;
  /** What this destination is for, in one line. Scope, not a promise. */
  purpose: string;
}

const PENDING_PAGES: Record<CommandPendingVariant, PendingSpec> = {
  allowance: {
    title: 'Allowance',
    purpose: 'Read-only allowance and spend blocks, each labelled with its measurement basis.',
  },
  models: {
    title: 'Models',
    purpose: 'The model and seat matrix, projected from the estate telemetry.',
  },
  pipeline: {
    title: 'Pipeline',
    purpose: 'The coordination projection: what is queued, what is running, what is waiting.',
  },
  decisions: {
    title: 'Decisions',
    purpose: 'The coordination projection and the human gates beside it.',
  },
};

/**
 * An honest scaffold for a destination that exists but is not built yet.
 *
 * It reads nothing, shows no figures and offers no controls: an operator who
 * types the URL gets the truth ("not built yet") instead of an empty dashboard
 * that reads like a working one. The figures arrive with the packet that builds
 * this page, never with this scaffold.
 */
export function CommandPendingPage({ variant }: { variant: CommandPendingVariant }) {
  const spec = PENDING_PAGES[variant];

  return (
    <section aria-label={spec.title}>
      <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-800 pb-2">
        <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-white">{spec.title}</h2>
        <span className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70">Pending</span>
      </div>

      <div className="rounded-lg border border-gray-800 bg-gray-900/20 p-4">
        <p className="text-xs font-mono text-gray-400">
          No source yet — this page reads nothing and shows no figures.
        </p>
        <p className="mt-3 text-xs font-mono text-gray-400">
          <span className="text-gray-300">Scope:</span> {spec.purpose}
        </p>
        <p className="mt-3 text-[10px] font-mono text-gray-400">
          &gt; The scaffold is deliberately empty. It marks the destination as reachable and honest.
        </p>
      </div>
    </section>
  );
}
